/**
 * useStudentActivityState — ce qui est à l'écran d'activité seul (`useReducer`) : la page de la copie
 * ouverte en grand, la question « Supprimer la photo ? », l'enregistreur ouvert, la caméra du
 * navigateur ouverte. Le reste (la copie, la finalisation, le chrono) est au moteur.
 */
import { useReducer } from "react"

export type StudentActivityUiState = {
    /** La page ouverte dans la visionneuse, son index. */
    openPhoto: number | null
    /** La pièce dont on demande la suppression (sa clé). */
    confirmDelete: string | null
    recorderOpen: boolean
    /** La caméra du navigateur (`StudentCopyCamera`), le temps d'une photo. */
    cameraOpen: boolean
}

export type StudentActivityUiAction =
    | { type: "OPEN_PHOTO"; index: number }
    | { type: "CLOSE_PHOTO" }
    | { type: "ASK_DELETE"; key: string }
    | { type: "CANCEL_DELETE" }
    | { type: "DELETE_DONE" }
    | { type: "OPEN_RECORDER" }
    | { type: "CLOSE_RECORDER" }
    | { type: "OPEN_CAMERA" }
    | { type: "CLOSE_CAMERA" }
    | { type: "RESET" }

export const INITIAL_ACTIVITY_UI: StudentActivityUiState = {
    openPhoto: null,
    confirmDelete: null,
    recorderOpen: false,
    cameraOpen: false,
}

export function studentActivityUiReducer(
    state: StudentActivityUiState,
    action: StudentActivityUiAction
): StudentActivityUiState {
    switch (action.type) {
        case "OPEN_PHOTO":
            return { ...state, openPhoto: action.index }
        case "CLOSE_PHOTO":
            return state.openPhoto == null ? state : { ...state, openPhoto: null }
        case "ASK_DELETE":
            // La question prend la place de la visionneuse (comme aujourd'hui).
            return { ...state, confirmDelete: action.key, openPhoto: null }
        case "CANCEL_DELETE":
        case "DELETE_DONE":
            return state.confirmDelete == null ? state : { ...state, confirmDelete: null }
        case "OPEN_RECORDER":
            return { ...state, recorderOpen: true }
        case "CLOSE_RECORDER":
            return state.recorderOpen ? { ...state, recorderOpen: false } : state
        case "OPEN_CAMERA":
            return { ...state, cameraOpen: true }
        case "CLOSE_CAMERA":
            return state.cameraOpen ? { ...state, cameraOpen: false } : state
        case "RESET":
            return INITIAL_ACTIVITY_UI
        default:
            return state
    }
}

export function useStudentActivityState() {
    return useReducer(studentActivityUiReducer, INITIAL_ACTIVITY_UI)
}
